import { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "./ui/card";
import { Separator } from "./ui/separator";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import {
  Book,
  Database,
  FileText,
  HardDrive,
  Landmark,
  LayoutDashboard,
  Lock,
  Search,
  Server,
  Settings,
  Users,
  Workflow,
  ChevronRight,
} from "lucide-react";
import { processMermaidDiagrams } from "@/utils/mermaidProcessor";
import "./DocumentationStyles.css";

// Definição das categorias de documentação
const categories = [
  {
    id: "visao-geral",
    name: "Visão Geral",
    icon: <Book className="h-5 w-5" />,
    items: [
      { id: "readme", name: "Introdução", file: "README.md", icon: <Book /> },
      {
        id: "arquitetura",
        name: "Arquitetura",
        file: "Arquitetura.md",
        icon: <Workflow />,
      },
    ],
  },
  {
    id: "modulos",
    name: "Módulos",
    icon: <Server className="h-5 w-5" />,
    items: [
      {
        id: "dashboard",
        name: "Dashboard",
        file: "Dashboard.md",
        icon: <LayoutDashboard />,
      },
      {
        id: "modelos",
        name: "Gerenciamento de Modelos",
        file: "Gerenciamento_Modelos.md",
        icon: <Server />,
      },
      {
        id: "agentes",
        name: "Gerenciamento de Agentes",
        file: "Gerenciamento_Agentes.md",
        icon: <Settings />,
      },
      {
        id: "especialistas",
        name: "Gerenciamento de Especialistas",
        file: "Gerenciamento_Produtos.md",
        icon: <HardDrive />,
      },
      {
        id: "clientes",
        name: "Gerenciamento de Clientes",
        file: "Gerenciamento_Clientes.md",
        icon: <Landmark />,
      },
      {
        id: "usuarios",
        name: "Gerenciamento de Usuários",
        file: "Gerenciamento_Usuarios.md",
        icon: <Users />,
      },
      {
        id: "login",
        name: "Login e Autenticação",
        file: "Login_Autenticacao.md",
        icon: <Lock />,
      },
      {
        id: "monitoramento-apis",
        name: "Monitoramento de APIs",
        file: "Monitoramento_APIs.md",
        icon: <FileText />,
      },
      {
        id: "monitoramento-custos",
        name: "Monitoramento de Custos",
        file: "Monitoramento_Custos.md",
        icon: <FileText />,
      },
      {
        id: "monitoramento-tokens",
        name: "Monitoramento de Tokens",
        file: "Monitoramento_Tokens.md",
        icon: <FileText />,
      },
      {
        id: "auditoria",
        name: "Auditoria",
        file: "Auditoria.md",
        icon: <FileText />,
      },
    ],
  },
  {
    id: "banco-de-dados",
    name: "Banco de Dados",
    icon: <Database className="h-5 w-5" />,
    items: [
      {
        id: "estrutura-banco",
        name: "Estrutura do Banco",
        file: "Estrutura_Banco.md",
        icon: <Database />,
      },
      {
        id: "migracoes",
        name: "Migrações",
        file: "Migracoes.md",
        icon: <Database />,
      },
    ],
  },
  {
    id: "guias",
    name: "Guias",
    icon: <FileText className="h-5 w-5" />,
    items: [
      {
        id: "instalacao",
        name: "Guia de Instalação",
        file: "Guia_Instalacao.md",
        icon: <FileText />,
      },
      {
        id: "desenvolvimento",
        name: "Guia de Desenvolvimento",
        file: "Guia_Desenvolvimento.md",
        icon: <FileText />,
      },
      {
        id: "administracao",
        name: "Guia de Administração",
        file: "Guia_Administracao.md",
        icon: <FileText />,
      },
      {
        id: "api-reference",
        name: "Referência da API",
        file: "API_Reference.md",
        icon: <FileText />,
      },
      {
        id: "como-contribuir",
        name: "Como Contribuir",
        file: "Como_Contribuir.md",
        icon: <FileText />,
      },
      {
        id: "glossario",
        name: "Glossário",
        file: "Glossario.md",
        icon: <FileText />,
      },
    ],
  },
];

// Componente principal de documentação
const Documentation = () => {
  const [activeCategory, setActiveCategory] = useState("visao-geral");
  const [activeDoc, setActiveDoc] = useState("readme");
  const [searchTerm, setSearchTerm] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [filteredItems, setFilteredItems] = useState<
    { id: string; name: string; file: string; icon: JSX.Element }[]
  >([]);
  const [isProcessing, setIsProcessing] = useState(true);
  const [isMermaidLoaded, setIsMermaidLoaded] = useState(false);

  // Referência para o conteúdo markdown
  const markdownContentRef = useRef<HTMLDivElement>(null);

  // Lazy loading do Mermaid - carrega apenas quando Documentation é montado
  useEffect(() => {
    const loadMermaid = async () => {
      if (!window.mermaid) {
        try {
          const { initializeMermaid } = await import("@/utils/mermaidInit");
          await initializeMermaid();
          setIsMermaidLoaded(true);
        } catch (error) {
        }
      } else {
        setIsMermaidLoaded(true);
      }
    };

    loadMermaid();
  }, []);

  // Função para carregar o arquivo Markdown
  const loadMarkdown = useCallback(
    async (file: string) => {
      try {
        setIsProcessing(true);

        // Normalizar o caminho do arquivo - remover prefixo /documentation/ se existir
        let formattedPath = file;
        if (formattedPath.startsWith("/documentation/")) {
          formattedPath = formattedPath.substring("/documentation/".length);
        }

        // Garantir que não comece com barra
        if (formattedPath.startsWith("/")) {
          formattedPath = formattedPath.substring(1);
        }

        const docPath = `/documentation/${formattedPath}`;

        const response = await fetch(docPath);

        if (!response.ok) {
          throw new Error(`Erro ao carregar ${file}: ${response.status}`);
        }

        const text = await response.text();
        setMarkdown(text);

        // Atualiza o DOM após o conteúdo ser renderizado
        setTimeout(() => {
          try {
            // Usa o processador de diagramas Mermaid apenas se carregado
            if (isMermaidLoaded && window.mermaid) {
              processMermaidDiagrams();
            }

            // Realça os blocos de código não-Mermaid
            if (window.hljs) {
              document
                .querySelectorAll("pre code:not(.language-mermaid)")
                .forEach((block) => {
                  window.hljs.highlightElement(block);
                });
            }

            // Prepara os links após renderizar
            prepareLinks();
          } catch (renderError) {
          } finally {
            setIsProcessing(false);
          }
        }, 250); // Aumentado o tempo para garantir que o DOM esteja pronto
      } catch (error) {
        setMarkdown(
          "# Erro ao carregar documentação\n\nNão foi possível carregar o arquivo de documentação solicitado."
        );
        setIsProcessing(false);
      }
    },
    [isMermaidLoaded]
  );

  // Função para preparar links usando delegação de eventos
  const prepareLinks = useCallback(() => {
    if (!markdownContentRef.current) return;

    // Processa links para estilização visual
    const links = markdownContentRef.current.querySelectorAll("a");
    links.forEach((link) => {
      const href = link.getAttribute("href");
      if (href && (href.endsWith(".md") || href.includes("/documentation/"))) {
        // Marca visualmente como link interno
        link.classList.add("doc-internal-link");
      } else if (href && href.startsWith("http")) {
        // Links externos abrem em nova aba
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      }
    });
  }, []);

  // Handler para cliques em links usando delegação de eventos
  const handleContentClick = useCallback(
    (e: MouseEvent) => {
      // Verifica se o clique foi em um link
      const target = e.target as HTMLElement;
      const link = target.closest("a") as HTMLAnchorElement;

      if (!link) return; // Não é um clique em link

      const href = link.getAttribute("href");
      if (!href) return;

      // Apenas processa links internos de documentação
      if (href.endsWith(".md") || href.includes("/documentation/")) {
        e.preventDefault();

        // Feedback visual para o usuário - piscando o link
        link.style.backgroundColor = "rgba(67, 56, 202, 0.1)";
        setTimeout(() => {
          link.style.backgroundColor = "transparent";
        }, 300);

        // Normaliza o caminho para obter o nome do arquivo
        let filePath = href;

        // Remove prefixos de caminho
        if (filePath.startsWith("/documentation/")) {
          filePath = filePath.substring("/documentation/".length);
        } else if (filePath.startsWith("./")) {
          filePath = filePath.substring(2);
        }

        // Procura pelo documento nas categorias
        const allItems = categories.flatMap((cat) => cat.items);
        const matchingItem = allItems.find(
          (item) =>
            item.file === filePath ||
            item.file === filePath.replace("./", "") ||
            item.file === filePath.replace("./", "").toLowerCase() ||
            filePath.includes(item.file.toLowerCase())
        );

        if (matchingItem) {
          // Encontra a categoria do item
          const category = categories.find((cat) =>
            cat.items.some((i) => i.id === matchingItem.id)
          );

          if (category) {
            setActiveCategory(category.id);
          }
          setActiveDoc(matchingItem.id);
        } else {
          // Se não encontrou no menu, apenas carrega o arquivo
          loadMarkdown(filePath);
        }
      }
    },
    [loadMarkdown]
  );

  // Efeito para monitorar a atualização do DOM do conteúdo Markdown
  useEffect(() => {
    // Monitorar quando o conteúdo Markdown é atualizado
  }, [markdown]);

  // Efeito que aplica a delegação de eventos
  useEffect(() => {
    // Adiciona o handler de delegação de eventos ao container quando ele for montado
    if (markdownContentRef.current) {
      markdownContentRef.current.addEventListener("click", handleContentClick);
    }

    // Limpeza ao desmontar
    return () => {
      if (markdownContentRef.current) {
        markdownContentRef.current.removeEventListener(
          "click",
          handleContentClick
        );
      }
    };
  }, [handleContentClick]);

  // Efeito para carregar o documento inicial
  useEffect(() => {
    const initialCategory = categories.find((cat) => cat.id === activeCategory);
    if (initialCategory) {
      const initialDoc = initialCategory.items[0];
      setActiveDoc(initialDoc.id);
      loadMarkdown(initialDoc.file);
    }
  }, [activeCategory, loadMarkdown]);

  // Efeito para carregar o documento quando o activeDoc muda
  useEffect(() => {
    const allItems = categories.flatMap((cat) => cat.items);
    const currentDoc = allItems.find((item) => item.id === activeDoc);

    if (currentDoc) {
      loadMarkdown(currentDoc.file);
    }
  }, [activeDoc, loadMarkdown]);

  // Efeito para filtrar itens baseado na busca
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredItems([]);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const results = categories
      .flatMap((cat) => cat.items)
      .filter(
        (item) =>
          item.name.toLowerCase().includes(lowerSearchTerm) ||
          item.id.toLowerCase().includes(lowerSearchTerm)
      );

    setFilteredItems(results);
  }, [searchTerm]);

  // Função para renderizar o conteúdo Markdown
  const renderMarkdown = () => {
    if (!window.markdownit)
      return <div>Carregando renderizador de Markdown...</div>;

    try {
      // Configurar markdown-it com plugins necessários
      const md = window.markdownit({
        html: true,
        linkify: true,
        typographer: true,
        highlight: function (str, lang) {
          // Identificar vários tipos de diagramas Mermaid
          const isMermaid =
            lang === "mermaid" ||
            str.trim().startsWith("graph ") ||
            str.trim().startsWith("flowchart ") ||
            str.trim().startsWith("sequenceDiagram") ||
            str.trim().startsWith("classDiagram") ||
            str.trim().startsWith("erDiagram") ||
            str.trim().startsWith("gantt") ||
            str.trim().startsWith("pie") ||
            str.trim().startsWith("stateDiagram");

          if (isMermaid) {
            // Retorna um bloco com classe mermaid direto
            return `<div class="mermaid">${str}</div>`;
          }

          // Syntax highlighting para outros blocos de código
          if (window.hljs && lang && window.hljs.getLanguage(lang)) {
            try {
              const highlighted = window.hljs.highlight(str, {
                language: lang,
              }).value;
              return `<pre><code class="hljs language-${lang}">${highlighted}</code></pre>`;
            } catch (error) {
            }
          }

          // Fallback para código sem highlighting
          return `<pre><code class="language-${
            lang || "text"
          }">${md.utils.escapeHtml(str)}</code></pre>`;
        },
      });

      // Renderiza o markdown
      const renderedContent = md.render(markdown);

      return (
        <div
          className="markdown-content max-w-none"
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />
      );
    } catch (error) {
      return (
        <div className="text-destructive">Erro ao renderizar o conteúdo.</div>
      );
    }
  };

  const currentCategory = categories.find((cat) => cat.id === activeCategory);

  return (
    <div className="container mx-auto p-4">
      <div className="flex flex-col space-y-4">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <h1 className="text-3xl font-bold">Documentação AI-Visor</h1>

          <div className="relative w-full md:w-64">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              id="doc-search"
              name="doc-search"
              placeholder="Buscar na documentação..."
              className="w-full pl-8 py-2 rounded-md border border-input bg-background"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <Separator />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Sidebar de Navegação */}
          <div className="md:col-span-3">
            <Card className="h-full overflow-hidden">
              <div className="p-4">
                <h2 className="text-lg font-semibold mb-4">Categorias</h2>

                {/* Botões das categorias */}
                <div className="flex flex-col space-y-2 mb-4">
                  {categories.map((category) => (
                    <Button
                      key={category.id}
                      variant={
                        activeCategory === category.id ? "default" : "outline"
                      }
                      className="justify-start"
                      onClick={() => setActiveCategory(category.id)}
                    >
                      <span className="mr-2">{category.icon}</span>
                      <span>{category.name}</span>
                    </Button>
                  ))}
                </div>

                <Separator className="my-4" />

                {/* Lista de documentos da categoria selecionada ou resultados da busca */}
                {searchTerm.trim() !== "" && filteredItems.length > 0 ? (
                  <>
                    <h3 className="text-sm font-medium mb-2">
                      Resultados da busca
                    </h3>
                    <ScrollArea className="h-[50vh]">
                      <div className="space-y-1 py-2">
                        {filteredItems.map((item) => (
                          <Button
                            key={item.id}
                            variant={
                              activeDoc === item.id ? "secondary" : "ghost"
                            }
                            className="w-full justify-start text-left"
                            onClick={() => {
                              setActiveDoc(item.id);
                              const category = categories.find((cat) =>
                                cat.items.some((i) => i.id === item.id)
                              );
                              if (category) {
                                setActiveCategory(category.id);
                              }
                            }}
                          >
                            <span className="mr-2">{item.icon}</span>
                            <span className="truncate">{item.name}</span>
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  </>
                ) : currentCategory ? (
                  <>
                    <h3 className="text-sm font-medium mb-2">
                      {currentCategory.name}
                    </h3>
                    <ScrollArea className="h-[50vh]">
                      <div className="space-y-1 py-2">
                        {currentCategory.items.map((item) => (
                          <Button
                            key={item.id}
                            variant={
                              activeDoc === item.id ? "secondary" : "ghost"
                            }
                            className="w-full justify-start text-left"
                            onClick={() => setActiveDoc(item.id)}
                          >
                            <span className="mr-2">{item.icon}</span>
                            <span className="truncate">{item.name}</span>
                          </Button>
                        ))}
                      </div>
                    </ScrollArea>
                  </>
                ) : null}
              </div>
            </Card>
          </div>

          {/* Conteúdo principal */}
          <div className="md:col-span-9">
            <Card className="h-full">
              {isProcessing ? (
                <div className="flex items-center justify-center p-12 h-[75vh]">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                    <p className="text-sm text-muted-foreground">
                      Carregando documento...
                    </p>
                  </div>
                </div>
              ) : (
                <ScrollArea className="h-[75vh]">
                  <div className="p-6">
                    {/* Breadcrumbs de navegação */}
                    <div className="flex items-center text-sm text-muted-foreground mb-6">
                      {currentCategory && (
                        <>
                          <span>{currentCategory.name}</span>
                          <ChevronRight className="h-4 w-4 mx-1" />
                          <span className="font-medium">
                            {currentCategory.items.find(
                              (item) => item.id === activeDoc
                            )?.name || ""}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Conteúdo Markdown */}
                    <div
                      className="markdown-content"
                      ref={markdownContentRef}
                      onClick={(e) =>
                        handleContentClick(e as unknown as MouseEvent)
                      }
                    >
                      {renderMarkdown()}
                    </div>
                  </div>
                </ScrollArea>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
